// src/components/admin/ComparePromptVersions.jsx
import React, { useMemo, useState, useEffect } from "react";
import { Row, Col, Form, Badge, Modal, Button } from "react-bootstrap";
import ReactDiffViewer from "react-diff-viewer-continued";

export default function ComparePromptVersions({ show, onHide, versions = [] }) {
  const sorted = useMemo(
    () =>
      [...versions].sort(
        (a, b) => Number(a.version ?? 0) - Number(b.version ?? 0)
      ),
    [versions]
  );

  const [baseId, setBaseId] = useState(null);
  const [compareId, setCompareId] = useState(null);

  // Reset defaults every time modal opens / versions change
  useEffect(() => {
    if (!show || sorted.length === 0) return;
    const first = sorted[0]?.id ?? null;
    const last = sorted[sorted.length - 1]?.id ?? null;

    setBaseId(first);
    setCompareId(last);
  }, [show, sorted]);

  const baseVersion =
    sorted.find((v) => v.id === baseId) || sorted[0] || null;
  const compareVersion =
    sorted.find((v) => v.id === compareId) ||
    sorted[sorted.length - 1] ||
    null;

  const disableDiff = !baseVersion || !compareVersion;

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="xl"
      centered
      scrollable
      backdrop="static"
    >
      <Modal.Header closeButton>
        <div className="w-100 d-flex justify-content-between align-items-center flex-wrap gap-2">
          <div className="fw-semibold">Compare Prompt Versions</div>
          {baseVersion && compareVersion && (
            <div className="small text-muted">
              <Badge bg="secondary" pill className="me-2">
                Base: v{Number(baseVersion.version)}
              </Badge>
              <Badge bg="primary" pill>
                Compare: v{Number(compareVersion.version)}
              </Badge>
            </div>
          )}
        </div>
      </Modal.Header>

      <Modal.Body>
        {sorted.length < 2 ? (
          <div className="text-muted small">
            Need at least two versions to compare.
          </div>
        ) : (
          <>
            {/* Version selectors */}
            <Row className="g-3 mb-3">
              <Col md={6}>
                <Form.Group controlId="compare-base-version">
                  <Form.Label className="small text-muted mb-1">
                    Base Version (left)
                  </Form.Label>
                  <Form.Select
                    size="sm"
                    value={baseId || ""}
                    onChange={(e) => setBaseId(e.target.value || null)}
                  >
                    {sorted.map((v) => (
                      <option key={v.id} value={v.id}>
                        v{Number(v.version)}{" "}
                        {v.active ? "(ACTIVE)" : ""} —{" "}
                        {v.updated_at || v.created_at || "—"}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group controlId="compare-compare-version">
                  <Form.Label className="small text-muted mb-1">
                    Compared Version (right)
                  </Form.Label>
                  <Form.Select
                    size="sm"
                    value={compareId || ""}
                    onChange={(e) => setCompareId(e.target.value || null)}
                  >
                    {sorted.map((v) => (
                      <option key={v.id} value={v.id}>
                        v{Number(v.version)}{" "}
                        {v.active ? "(ACTIVE)" : ""} —{" "}
                        {v.updated_at || v.created_at || "—"}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            {/* Diff viewer */}
            {disableDiff ? (
              <div className="text-muted small">
                Select both base and compared versions to see the diff.
              </div>
            ) : (
              <div
                className="border rounded"
                style={{
                  maxHeight: 500,
                  overflow: "auto",
                  fontFamily: "monospace",
                  fontSize: "0.85rem",
                }}
              >
                <ReactDiffViewer
                  oldValue={baseVersion.prompt_text || ""}
                  newValue={compareVersion.prompt_text || ""}
                  splitView={true}
                  showDiffOnly={false}
                  extraLinesSurroundingDiff={3}
                />
              </div>
            )}
          </>
        )}
      </Modal.Body>

      <Modal.Footer className="border-0">
        <Button variant="outline-secondary" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
